import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../services/api';

const PAGE_SIZE = 50;

const formatDateTime = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
};

const formatFiat = (amountFiat, currency) => {
  if (amountFiat === null || amountFiat === undefined || amountFiat === '') return '–';
  const cur = (currency || '').toUpperCase();
  const cents = typeof amountFiat === 'string' ? Number(amountFiat) : amountFiat;
  if (!Number.isFinite(cents)) return cur ? `${amountFiat} ${cur}` : String(amountFiat);

  const value = (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return cur ? `${value} ${cur}` : value;
};

const typeLabel = (type) => {
  if (type === 'topup') return 'Top‑off';
  if (type === 'subscription_invoice') return 'Subscription';
  return type || '–';
};

const AdminTokenLedgerPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [provider, setProvider] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-token-ledger', { page, search, paymentType, provider }],
    queryFn: async () => {
      const params = { page, page_size: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (paymentType) params.payment_type = paymentType;
      if (provider) params.provider = provider;
      const response = await adminAPI.getTokenLedger(params);
      return response.data;
    },
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const pagination = data?.pagination || { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 };

  const canGoPrev = useMemo(() => pagination.page > 1, [pagination.page]);
  const canGoNext = useMemo(
    () => pagination.totalPages && pagination.page < pagination.totalPages,
    [pagination.page, pagination.totalPages]
  );

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Token Ledger</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Subscription credits and one-time top-offs across the system.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Search
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by username or email"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Type
          </label>
          <select
            value={paymentType}
            onChange={(e) => {
              setPaymentType(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="subscription_invoice">Subscription</option>
            <option value="topup">Top‑off</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Provider
          </label>
          <select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="stripe">Stripe</option>
            <option value="revenuecat">RevenueCat</option>
          </select>
        </div>
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400">
        Total rows: <span className="font-medium">{pagination.total}</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Loading token ledger...</div>
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Failed to load token ledger.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Created</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">User</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Provider</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Status</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-200">Tokens</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Fiat</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Payment ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {items.map((row) => {
                  const user = row?.user || {};
                  return (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400 text-xs">
                        {formatDateTime(row.createdAt)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                        <div className="flex flex-col">
                          <span className="text-gray-900 dark:text-gray-100">{user.username || '–'}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{user.email || '–'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                        {(row.provider || '–').toUpperCase()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                        {typeLabel(row.type)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700">
                          {row.status || '–'}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right font-medium text-gray-900 dark:text-gray-100">
                        {typeof row.tokensGranted === 'number' ? row.tokensGranted.toLocaleString() : '–'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">
                        {formatFiat(row.amountFiat, row.currency)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                        {row.providerPaymentId || '–'}
                      </td>
                    </tr>
                  );
                })}

                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No entries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages || 1}
            </div>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => canGoPrev && setPage((p) => Math.max(1, p - 1))}
                disabled={!canGoPrev}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoPrev
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => canGoNext && setPage((p) => p + 1)}
                disabled={!canGoNext}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoNext
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminTokenLedgerPage;


